import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ZITI_DATA_SERVICE, ZitiDataService } from 'ziti-console-lib';
import type { ConfigPayload } from './template-form.util';
import { CreationStatus } from './service-template.model';
import {
    buildMicrosegmentConfigPayloads,
    HOST_V1_CONFIG_TYPE_ID,
    HOST_V1_CONFIG_TYPE_NAME,
    INTERCEPT_V1_CONFIG_TYPE_ID,
    INTERCEPT_V1_CONFIG_TYPE_NAME,
    MicrosegmentConfigTypeIds,
    MicrosegmentDraft,
} from './microsegment-mapping.util';
import { CreationResult, ServiceFromTemplateService } from './service-from-template.service';
import { buildSimpleServiceConfigPayloads, SimpleServiceDraft } from './simple-service-mapping.util';

/**
 * How the service's configs are shaped:
 * - `intercept` (default): a passthrough service built from the intercept the operator is authoring
 *   (a collection of addresses/ports). A single address + single port yields a concrete host at that
 *   same address; anything broader yields a forwarding host. Carries a {@link MicrosegmentDraft}.
 * - `fixed-host` (advanced): a single intercept with an explicit, possibly different, host
 *   address/port. Carries a {@link SimpleServiceDraft}.
 */
export type SimpleServiceCreateDraft =
    | { mode: 'intercept'; draft: MicrosegmentDraft }
    | { mode: 'fixed-host'; draft: SimpleServiceDraft };

export interface SimpleServiceCreateInput {
    draft: SimpleServiceCreateDraft;
    /** Drop the intercept config: the client dials through an embedded SDK, with no tunneler intercept. */
    sdkOnlyDial: boolean;
    /** Drop the host config: an embedded SDK binds the service, with no tunneler host. */
    sdkOnlyBind: boolean;
    /** Attributes to tag the service with (for grouping). */
    serviceRoleAttributes: string[];
    /** Identities that can access (dial) the service: picked names + role attributes. */
    dialNamedAttributes: string[];
    dialRoleAttributes: string[];
    /** Identities that host (bind) the service: picked names + role attributes. */
    bindNamedAttributes: string[];
    bindRoleAttributes: string[];
    encryptionRequired: boolean;
    /** Extra OpenZiti tags to stamp on the created service AND its configs - e.g. to mark the workflow that
     *  created them, so a feature can later find just those resources (see MICROSEGMENT_SOURCE_TAG_KEY). */
    tags?: Record<string, any>;
}

/** Tags a service created through the simple-service flow, matching the legacy wizard. */
export const SIMPLE_SERVICE_TYPE_TAG = 'simple';

/**
 * Turns a simple-service draft into the real OpenZiti entities: resolves the controller's
 * intercept.v1 / host.v1 config-type ids and the Dial/Bind identity ids, then hands the prebuilt
 * payloads to the shared creation pipeline (service + configs + dial/bind policies).
 */
@Injectable({ providedIn: 'root' })
export class SimpleServiceCreateService {
    constructor(
        @Inject(ZITI_DATA_SERVICE) private ziti: ZitiDataService,
        private orchestrator: ServiceFromTemplateService
    ) {}

    async create(input: SimpleServiceCreateInput): Promise<{
        status$: Observable<CreationStatus[]>;
        result: Promise<CreationResult>;
    }> {
        const ids = await this.resolveConfigTypeIds();
        const { serviceName, configPayloads } = this.buildConfigPayloads(input, ids);

        // Stamp the caller's tags (e.g. the microsegmentation-source marker) onto every config too, not
        // just the service, so a feature that reads configs (like Traffic Analysis coverage) can find them.
        if (input.tags) {
            configPayloads.forEach((p) => (p.tags = { ...(p.tags ?? {}), ...input.tags }));
        }

        // Named identities resolve to `@<id>` selectors; role attributes pass through (the
        // orchestrator prefixes a bare attribute with `#`).
        const dialPolicyAttributes = [
            ...(await this.resolveIdentitySelectors(input.dialNamedAttributes)),
            ...input.dialRoleAttributes,
        ];
        const bindPolicyAttributes = [
            ...(await this.resolveIdentitySelectors(input.bindNamedAttributes)),
            ...input.bindRoleAttributes,
        ];

        return this.orchestrator.createServiceFromConfigs({
            serviceName,
            configPayloads,
            dialPolicyAttributes,
            bindPolicyAttributes,
            serviceRoleAttributes: input.serviceRoleAttributes,
            encryptionRequired: input.encryptionRequired,
            serviceTags: { 'service-type': SIMPLE_SERVICE_TYPE_TAG, ...(input.tags ?? {}) },
        });
    }

    /**
     * Pick the config builder for the draft's mode, then drop a side's config for an SDK-only
     * client or host. Dropping lives here (not in the pure builders) so there is one authority
     * over which configs a service gets, regardless of how the addresses were shaped. The side is
     * matched by the shared config-name suffix, which both builders follow.
     */
    private buildConfigPayloads(
        input: SimpleServiceCreateInput,
        ids: MicrosegmentConfigTypeIds
    ): { serviceName: string; configPayloads: ConfigPayload[] } {
        const spec = input.draft;
        const serviceName = spec.draft.serviceName;
        const built =
            spec.mode === 'fixed-host'
                ? buildSimpleServiceConfigPayloads(spec.draft, ids)
                : buildMicrosegmentConfigPayloads(spec.draft, ids);

        const configPayloads = built.filter((payload) => {
            if (input.sdkOnlyDial && payload.name.endsWith('-intercept-config')) {
                return false;
            }
            if (input.sdkOnlyBind && payload.name.endsWith('-host-config')) {
                return false;
            }
            return true;
        });

        return { serviceName, configPayloads };
    }

    /** Resolve intercept.v1 / host.v1 config-type ids by name, falling back to the well-known ids. */
    async resolveConfigTypeIds(): Promise<MicrosegmentConfigTypeIds> {
        try {
            const res: any = await this.ziti.get('config-types', this.pagingClone(), []);
            const list: any[] = res?.data || res || [];
            const findId = (name: string) => list.find((c) => c?.name === name)?.id;
            return {
                interceptConfigTypeId: findId(INTERCEPT_V1_CONFIG_TYPE_NAME) || INTERCEPT_V1_CONFIG_TYPE_ID,
                hostConfigTypeId: findId(HOST_V1_CONFIG_TYPE_NAME) || HOST_V1_CONFIG_TYPE_ID,
            };
        } catch {
            // The mapper falls back to the well-known ids when none are supplied.
            return {};
        }
    }

    /** Resolve identity names to `@<id>` role selectors (best effort: `@<name>` if a name can't be matched). */
    async resolveIdentitySelectors(names: string[]): Promise<string[]> {
        const selectors: string[] = [];
        for (const name of names) {
            selectors.push(await this.resolveIdentitySelector(name));
        }
        return selectors;
    }

    private async resolveIdentitySelector(name: string): Promise<string> {
        try {
            const paging = this.pagingClone();
            paging.noSearch = false;
            const filters = [
                { filterName: 'name', columnId: 'name', value: name, label: name, type: 'TEXTINPUT', verb: 'icontains' },
            ];
            const res: any = await this.ziti.get('identities', paging, filters);
            const list: any[] = res?.data || res || [];
            const match = list.find((i) => i?.name === name) || (list.length === 1 ? list[0] : undefined);
            return match?.id ? `@${match.id}` : `@${name}`;
        } catch {
            return `@${name}`;
        }
    }

    /** A shallow copy of the shared default paging so we never mutate it. */
    private pagingClone(): any {
        const def = (this.ziti as any).DEFAULT_PAGING;
        return def ? { ...def } : { page: 1, total: 100, sort: 'name', order: 'asc', filter: '', noSearch: true };
    }
}
